import { getUserSetting } from "features/Account/redux/userSettings/userSettingsMiddleware";
import { updateSettings } from "features/Account/redux/userSettings/userSettingsSlice";
import useEmbeddedOnVolumeChange, {
  type VolumeChangeEventData,
  type YouTubeVolumeChangeEvent,
} from "features/Assets/hooks/useEmbeddedOnVolumeChange";
import { type EmbeddedVideoSource } from "features/Assets/types";
import { useCallback, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useDispatch } from "react-redux";
import { markEmbeddedVideoAsCompleted, markEmbeddedVideoAsStarted } from "../../services/assetsService";
import styles from "./EmbeddedVideoPlayer.module.scss";
import { EmbeddedVideoUnavailable } from "./EmbeddedVideoUnavailable";
import playerConfig from "./playerConfig";

export interface EmbeddedVideoProps {
  videoUrl: string;
  source: EmbeddedVideoSource;
  embeddedVideoId: number;
  onEmbeddedVideoStarted?: () => void;
  onEmbeddedVideoEnded?: () => void;
  onSkip?: () => void;
}

export const EmbeddedVideoPlayer: React.FC<EmbeddedVideoProps> = props => {
  const { videoUrl, embeddedVideoId, source, ...handlers } = props;

  const initRate = Number(getUserSetting("playbackRate")) || 1;
  const initVolume = Number(getUserSetting("volume")) || 1;

  const playerRef = useRef<ReactPlayer | null>(null);
  const [internalPlayer, setInternalPlayer] = useState<unknown>();
  const [isValid, setIsValid] = useState(true);

  const onEmbeddedVideoStarted = useCallback(() => {
    markEmbeddedVideoAsStarted(embeddedVideoId);
  }, [embeddedVideoId]);

  const onEmbeddedVideoEnded = useCallback(() => {
    markEmbeddedVideoAsCompleted(embeddedVideoId);
  }, [embeddedVideoId]);

  const dispatch = useDispatch();

  /* istanbul ignore next */
  const onRateChange = useCallback(
    (newRate: number) => {
      if (newRate !== initRate) {
        dispatch(
          updateSettings({
            settings: {
              playbackRate: newRate,
            },
          }),
        );
      }
    },
    [dispatch, initRate],
  );

  /* istanbul ignore next */
  const onVolumeChange = useCallback(
    (newVolume: number) => {
      dispatch(
        updateSettings({
          settings: {
            volume: newVolume,
          },
        }),
      );
    },
    [dispatch],
  );

  /* istanbul ignore next */
  const handleVolumeChange = useCallback(
    (e: number | YouTubeVolumeChangeEvent | VolumeChangeEventData) => {
      let newVolume: number | undefined;
      if (typeof e === "number") {
        // Wistia event
        newVolume = e;
      } else if ("data" in e && e.data.volume !== undefined) {
        // YouTube event
        newVolume = e.data.muted ? 0 : e.data.volume / 100;
      } else if ("volume" in e) {
        // Vimeo event
        newVolume = e.muted ? 0 : e.volume;
      }

      if (newVolume !== undefined && newVolume !== initVolume) {
        onVolumeChange(newVolume);
      }
    },
    [onVolumeChange, initVolume],
  );

  /* istanbul ignore next */
  const onReady = () => {
    const internalPlayer = playerRef?.current?.getInternalPlayer();
    if (internalPlayer) {
      setInternalPlayer(internalPlayer);
    }
  };

  /* istanbul ignore next */
  useEmbeddedOnVolumeChange(source, internalPlayer, handleVolumeChange);

  const onError = () => {
    setIsValid(false);
  };

  return isValid ? (
    <ReactPlayer
      className={styles.reactPlayer}
      ref={playerRef}
      onReady={onReady}
      url={videoUrl}
      controls
      onStart={handlers.onEmbeddedVideoStarted ? handlers.onEmbeddedVideoStarted : onEmbeddedVideoStarted}
      onEnded={handlers.onEmbeddedVideoEnded ? handlers.onEmbeddedVideoEnded : onEmbeddedVideoEnded}
      height={"100%"}
      width={"100%"}
      volume={initVolume}
      playbackRate={initRate}
      onPlaybackRateChange={onRateChange}
      onError={onError}
      config={playerConfig}
    />
  ) : (
    <div className={styles.unavailable}>
      <EmbeddedVideoUnavailable action={handlers.onSkip} />
    </div>
  );
};
